import { Col, Pagination, Progress, Row, Table, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { MdPictureAsPdf } from "react-icons/md";
import CustomProgressBar from "../readings/CustomProgressBar";
import { useMediaQuery } from "react-responsive";
import moment from "moment";
import {
  AppDispatch,
  getMyOrganization,
  getNcdRiskReadingList,
  getNcdRiskReadingListRequest,
  Reading,
  useDynamicSelector } from
"@src/redux";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { startCase } from "lodash";
import { getAgeCalculate, getVitalColorCode, pluralize } from "@src/helpers/constants";
import { UpCircleOutlined } from "@ant-design/icons";
import { PiExport } from "react-icons/pi";
import NcdReadingsReportExportModal from "../readings/NcdReadingExport";

type AlignType = "left" | "right" | "center"; // Define AlignType manually

interface NcdReadingRiskListProps {
  ncdRiskType?: string;
}

const NcdReadingRiskList: React.FC<NcdReadingRiskListProps> = ({
  ncdRiskType
}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const navigate = useNavigate();
  const { result: readingList, loading: readingListLoading } = useSelector(
    getNcdRiskReadingList
  );
  const { result: myOrg } = useSelector(getMyOrganization);
  const { ncdRiskFilter } = useDynamicSelector("ncd_risk_analysis");

  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const handleViewDetails = (record: Reading) => {
    navigate(`/details/${record.subject?.id}`);
  };

  const openReadingReportModal = () => {
    setIsModalVisible(true);
  };

  const columns: any[] = [
  {
    title: pluralize(myOrg?.rootOrganization?.subjectLabel || ""),
    key: "worker",
    align: "center",
    fixed: "left",
    width: 150,
    render: (record: Reading) => {
      return (
        <Tooltip
          placement="bottom"
          getPopupContainer={() => document.body}
          title={
          <div style={{ width: "200px" }}>
                <strong
              onClick={() => handleViewDetails(record)}
              className="subject-user-name-container">

                  {record?.subject?.name}
                </strong>
                <hr />
                <span>{startCase(record?.subject?.subjectType)}</span>
                <br />
                <span>{startCase(record?.subject?.gender)}</span>
                <br />
                <span>
                  {getAgeCalculate(record?.subject?.dob?.toString() || "")}
                </span>
              </div>}>


            <div className="worker-container">
              <span className="worker-table-name">{record?.subject?.name}</span>
            </div>
          </Tooltip>);

    }
  },
  {
    title: "Date / Time",
    key: "datetime",
    align: "center" as AlignType,
    fixed: "left",
    width: 130,
    render: (record: Reading) => {
      return moment(record?.datetime).format("DD-MMM-YYYY / hh:mm A");
    }
  },
  {
    title: "Pulse Rate",
    key: "pulseRate",
    align: "center" as AlignType,
    width: 90,
    render: (record: Reading) =>
    <CustomProgressBar
      record={{
        value: record?.vincenseReading?.pulseValue,
        color: record?.vincenseReading?.pulseColor
      }} />


  },
  {
    title: "Skin Temp",
    key: "skinTemp",
    align: "center" as AlignType,
    width: 90,
    render: (record: Reading) =>
    record?.vincenseReading?.skinValue ?
    <CustomProgressBar
      record={{
        value: record?.vincenseReading?.skinValue
      }} /> :


    "-"

  },
  {
    title: "SpO2",
    key: "spo2",
    align: "center" as AlignType,
    width: 80,
    render: (record: Reading) =>
    <CustomProgressBar
      record={{
        value: record?.vincenseReading?.o2Value,
        color: record?.vincenseReading?.o2Color
      }} />


  },
  {
    title: "RR",
    key: "rr",
    align: "center" as AlignType,
    width: 70,
    render: (record: Reading) =>
    <CustomProgressBar
      record={{
        value: record?.vincenseReading?.rrValue,
        color: record?.vincenseReading?.rrColor
      }} />


  },
  {
    title: "BP",
    key: "bp",
    align: "center" as AlignType,
    width: 110,
    render: (record: Reading) =>
    record.bpReading?.systolicValue ||
    record.bpReading?.diastolicValue ?
    <div className="bp-progress-container">
            <div className="bp-value">
              <span>{record.bpReading?.systolicValue}</span>
              <Progress
          percent={100}
          showInfo={false}
          strokeColor={getVitalColorCode(record.bpReading?.systolicColor)}
          className="custom-progress" />
        
            </div>
            <span>/</span>
            <div className="bp-value">
              <span>{record.bpReading?.diastolicValue}</span>
              <Progress
          percent={100}
          showInfo={false}
          strokeColor={getVitalColorCode(record.bpReading?.diastolicColor)}
          className="custom-progress" />
        
            </div>
          </div> :

    "-"

  },

  {
    title: "Sugar",
    key: "sugar",
    align: "center" as AlignType,
    width: 110,
    render: (record: Reading) =>
    record?.glucometerReading?.randomValue &&
    record?.glucometerReading?.randomColor ?
    <CustomProgressBar
      record={{
        value: record?.glucometerReading?.randomValue,
        color: record?.glucometerReading?.randomColor
      }} /> :

    record?.glucometerReading?.fastingValue ||
    record?.glucometerReading?.ppValue ?
    <div className="bp-progress-container">
            <div className="bp-value">
              <span>{record?.glucometerReading?.fastingValue || "-"}</span>
              <Progress
          percent={100}
          showInfo={false}
          strokeColor={getVitalColorCode(
          record?.glucometerReading?.fastingColor)}

          className="custom-progress" />
        
            </div>
            <span>/</span>
            <div className="bp-value">
              <span>{record?.glucometerReading?.ppValue || "-"}</span>
              <Progress
          percent={100}
          showInfo={false}
          strokeColor={getVitalColorCode(record.glucometerReading?.ppColor)}
          className="custom-progress" />
        
            </div>
          </div> :

    "-"

  },
  // {
  //   title: "Sugar",
  //   key: "sugar",
  //   align: "center" as AlignType,
  //   render: (record: Reading) => (
  //     <CustomProgressBar
  //       record={{
  //         value: record?.glucometerReading?.randomValue,
  //         color: record?.glucometerReading?.randomColor,
  //       }}
  //     />
  //   ),
  // },
  {
    title: "BMI",
    key: "bmi",
    align: "center" as AlignType,
    width: 80,
    render: (record: Reading) =>
    <CustomProgressBar
      record={{
        value: record?.weighingReading?.bmiValue,
        color: record?.weighingReading?.bmiColor
      }} />


  },
  {
    title: "ISH Score",
    key: "ishScore",
    align: "center" as AlignType,
    width: 90,
    render: (record: Reading) =>
    record?.ishReading?.score && record?.ishReading?.color ?
    <CustomProgressBar
      record={{
        value: record?.ishReading?.score,
        color: record?.ishReading?.color
      }} /> :


    "-"

  }];


  const handleChange = (page: number, pageSize: number) => {
    if (!ncdRiskType || !ncdRiskFilter) return;
    dispatch(
      getNcdRiskReadingListRequest({
        filter: {
          ...ncdRiskFilter,
          riskType: ncdRiskType
        },
        pagination: {
          pageNumber: page,
          pageLimit: pageSize
        }
      })
    );
  };

  return (
    <div className="ncd-risk-list-wrapper">
      <Table
        className="readings-table ncd-risk-modal-table"
        dataSource={readingList?.items || []}
        columns={columns}
        pagination={false}
        loading={readingListLoading}
        bordered
        rowKey={(record: any) => record?.id}
        scroll={{
          x: 1000,
          y: "46vh"
        }} />

      <div className="ncd-risk-list-footer">
        <Tooltip title="Export">
          <button
            type="button"
            className="ncd-risk-export-btn"
            onClick={openReadingReportModal}
            aria-label="Export"
          >
            <PiExport size={16} />
          </button>
        </Tooltip>
        <Pagination
          size="small"
          onChange={handleChange}
          current={readingList?.pagination?.pageNumber}
          pageSize={readingList?.pagination?.pageLimit}
          total={readingList?.pagination?.totalCount}
          showSizeChanger={false} />
      </div>

      <NcdReadingsReportExportModal
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        ncdRiskType={ncdRiskType} />
    </div>);

};

export default NcdReadingRiskList;